import { useEffect, useRef } from "react";
import { toast } from "sonner";
import type { Familiarity } from "@itspip/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { YEAR_OPTIONS } from "@/lib/year-options";
import { AlignmentFields } from "@/features/objectives/alignment-fields";
import { ObjectiveTypeRadioCards } from "@/features/objectives/objective-type-field";
import { useCurrentUser } from "@/hooks/use-current-user";
import type { ObjectiveLevel } from "@/hooks/use-coach";
import type { WizardState, Quarter } from "./wizard-state";
import { FamiliarityControl } from "./familiarity-control";
import { useUpdateOkrProfile } from "./use-okr-profile";

interface SetupStepProps {
  state: WizardState;
  onChange: (next: WizardState) => void;
}

export function SetupStep({ state, onChange }: SetupStepProps) {
  const { currentUser } = useCurrentUser();
  const { setup } = state;
  const updateProfile = useUpdateOkrProfile();

  // Seed familiarity from the current user once — no overwrite on re-render.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (setup.familiarity === null && currentUser?.familiarity) {
      seededRef.current = true;
      onChange({
        ...state,
        setup: { ...setup, familiarity: currentUser.familiarity },
      });
    }
  }, [currentUser?.familiarity, setup, state, onChange]);

  const update = (patch: Partial<WizardState["setup"]>) => {
    onChange({ ...state, setup: { ...setup, ...patch } });
  };

  const activeFamiliarity: Familiarity =
    setup.familiarity ?? currentUser?.familiarity ?? "developing";

  const handleFamiliarityChange = (next: Familiarity) => {
    if (next === activeFamiliarity) return;
    update({ familiarity: next });
    updateProfile.mutate(next, {
      onSuccess: () => {
        toast.success("Familiarity updated");
      },
      onError: (err) => {
        toast.error(
          err instanceof Error ? err.message : "Couldn't save familiarity.",
        );
      },
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <FamiliarityControl
            value={activeFamiliarity}
            onChange={handleFamiliarityChange}
            pending={updateProfile.isPending}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_340px]">
        <Card>
          <CardHeader>
            <CardTitle>Set Up Your OKR</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="coach-level">Level</Label>
                <Select
                  value={setup.level ?? undefined}
                  onValueChange={(v) => update({ level: v as ObjectiveLevel })}
                >
                  <SelectTrigger id="coach-level">
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="department">Department</SelectItem>
                    <SelectItem value="team">Team</SelectItem>
                    <SelectItem value="individual">Individual</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="sm:col-span-2">
                <ObjectiveTypeRadioCards
                  value={setup.type}
                  onChange={(v) => update({ type: v })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="coach-year">Year</Label>
                <Select
                  value={setup.year.toString()}
                  onValueChange={(v) => update({ year: parseInt(v, 10) })}
                >
                  <SelectTrigger id="coach-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {YEAR_OPTIONS.map((y) => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="coach-quarter">Quarter</Label>
                <Select
                  value={setup.quarter}
                  onValueChange={(v) => update({ quarter: v as Quarter })}
                >
                  <SelectTrigger id="coach-quarter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annual">Annual</SelectItem>
                    <SelectItem value="Q1">Q1</SelectItem>
                    <SelectItem value="Q2">Q2</SelectItem>
                    <SelectItem value="Q3">Q3</SelectItem>
                    <SelectItem value="Q4">Q4</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {setup.level && (
              <AlignmentFields
                level={setup.level}
                parentObjectiveId={setup.parentObjectiveId}
                focusAreaId={setup.focusAreaId}
                tacticId={setup.tacticId}
                onParentObjectiveIdChange={(id) => update({ parentObjectiveId: id })}
                onFocusAreaIdChange={(id) => update({ focusAreaId: id })}
                onTacticIdChange={(id) => update({ tacticId: id })}
                isDepartmentHead={currentUser?.departmentHead ?? false}
              />
            )}
          </CardContent>
        </Card>

        <InfoSidebar />
      </div>
    </div>
  );
}

function InfoSidebar() {
  return (
    <>
    <Card className="bg-[hsl(var(--muted))]">
      <CardHeader>
        <CardTitle className="text-lg">What Makes a Great OKR?</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <section className="space-y-2">
          <h3 className="font-semibold">A good Objective is&hellip;</h3>
          <ul className="space-y-1 text-[hsl(var(--muted-foreground))]">
            <li>
              <span className="font-medium text-foreground">Significant</span> — a top priority that would meaningfully improve where you are today.
            </li>
            <li>
              <span className="font-medium text-foreground">Concrete</span> — clear and specific enough to generate action.
            </li>
            <li>
              <span className="font-medium text-foreground">Action-oriented</span> — the action is implied in how it&rsquo;s written.
            </li>
            <li>
              <span className="font-medium text-foreground">Inspiring</span> — simple, memorable, and moves you to act.
            </li>
          </ul>
        </section>

        <section className="space-y-2">
          <h3 className="font-semibold">Good Key Results are&hellip;</h3>
          <ul className="space-y-1 text-[hsl(var(--muted-foreground))]">
            <li>
              <span className="font-medium text-foreground">Specific and time-bound</span> — no fuzzy numbers or vague dates.
            </li>
            <li>
              <span className="font-medium text-foreground">Aggressive, yet realistic</span> — stretch far enough to create meaningful change.
            </li>
            <li>
              <span className="font-medium text-foreground">Measurable and verifiable</span> — anyone on the team can check the status.
            </li>
          </ul>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            Aim for 3&ndash;5 per Objective (2 minimum).
          </p>
        </section>

        <blockquote className="border-l-2 border-[hsl(var(--accent))] pl-3 italic text-[hsl(var(--muted-foreground))]">
          &ldquo;Ideas are easy. Execution is everything.&rdquo;
          <footer className="mt-1 text-xs not-italic">&mdash; John Doerr</footer>
        </blockquote>
      </CardContent>
    </Card>
    </>
  );
}

export function isSetupComplete(state: WizardState): boolean {
  return state.setup.level !== null && state.setup.type !== null;
}
